import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  Minus,
  Plus,
  ShieldCheck,
  Truck,
  ShoppingCart,
  Check,
  Package,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { formatRupiah, formatSold } from "../mock";
import { getProduct, getProducts } from "../api";
import { useCart } from "../context/CartContext";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { toast } = useToast();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    getProduct(id)
      .then((p) => {
        setProduct(p);
        setVariant(p.variants?.[0] || "");
        setActiveImg(0);
        setQty(1);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    getProducts()
      .then((all) => setRelated(all.filter((x) => x.id !== id).slice(0, 4)))
      .catch(() => setRelated([]));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E3F1EC] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-[#CFE7E0] border-t-[#004743] animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#E3F1EC] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#004743] font-bold text-xl">
            Produk tidak ditemukan
          </p>
          <Link
            to="/"
            className="text-[#0B7C6E] text-sm font-semibold mt-2 inline-block"
          >
            Kembali ke beranda
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [""];

  const handleAddToCart = () => {
    addItem(product, variant, qty);
    toast({
      title: "Ditambahkan ke keranjang!",
      description: `${product.name} (${variant}) x${qty}`,
    });
  };

  const handleBuyNow = () => {
    addItem(product, variant, qty);
    navigate("/keranjang");
  };

  return (
    <div className="min-h-screen bg-[#E3F1EC] relative overflow-hidden pb-24">
      <div className="absolute top-32 -right-32 w-96 h-96 bg-[#D4EEFB] rounded-full blur-3xl opacity-60 pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 pt-28 relative">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A6B63] hover:text-[#004743] bg-white/70 backdrop-blur px-4 py-2 rounded-full shadow-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Kembali Belanja
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* LEFT: gallery */}
          <div className="min-w-0">
            <div
              className="rounded-[2.5rem] overflow-hidden aspect-square shadow-xl shadow-green-900/10 relative group"
              style={{ backgroundColor: product.color }}
            >
              <img
                src={images[activeImg]}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <span className="absolute top-5 left-5 bg-[#FD7A18] text-xs font-bold text-white px-4 py-2 rounded-full shadow-md shadow-orange-500/25">
                {product.badge}
              </span>
            </div>

            {images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 rounded-2xl overflow-hidden shrink-0 border-2 transition-all ${
                      activeImg === i
                        ? "border-[#FD7A18]"
                        : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                    style={{ backgroundColor: product.color }}
                  >
                    <img
                      src={img}
                      alt={`thumb-${i}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { icon: ShieldCheck, label: "100% Original" },
                { icon: Truck, label: "Dikirim Hari Ini" },
                { icon: Package, label: "Packing Aman" },
              ].map((f) => (
                <div
                  key={f.label}
                  className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-md shadow-green-900/5"
                >
                  <f.icon className="w-5 h-5 text-[#0B7C6E]" />
                  <span className="text-[10px] font-bold text-[#4A6B63] text-center">
                    {f.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: detail */}
          <div className="bg-white rounded-[2.5rem] p-5 md:p-8 shadow-xl shadow-green-900/5 min-w-0">
            <p className="text-[10px] font-bold text-[#8AA59D] uppercase tracking-widest">
              {product.category}
            </p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#004743] mt-1 tracking-tight">
              {product.name}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-[#FFD66B] fill-[#FFD66B]" />
                ))}
              </div>
              <span className="text-sm font-bold text-[#004743]">
                {product.rating}
              </span>
              <span className="text-xs text-[#8AA59D]">
                • {formatSold(product.sold)} terjual
              </span>
            </div>

            <div className="flex items-end gap-3 mt-4 flex-wrap">
              <p className="text-3xl font-extrabold text-[#0B7C6E]">
                {formatRupiah(product.price)}
              </p>
              {product.oldPrice > 0 && (
                <p className="text-sm text-[#AEC6BE] line-through mb-1">
                  {formatRupiah(product.oldPrice)}
                </p>
              )}
            </div>

            <p className="text-sm text-[#6B857E] leading-relaxed mt-4">
              {product.description}
            </p>

            {product.variants?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-bold text-[#004743] mb-2.5">
                  Pilih Varian
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVariant(v)}
                      className={`max-w-full px-3.5 py-2.5 rounded-full text-xs font-bold border-2 transition-all duration-200 flex items-center gap-1.5 ${
                        variant === v
                          ? "bg-[#004743] text-white border-[#004743] shadow-md shadow-green-800/25"
                          : "bg-[#F0F7F5] text-[#4A6B63] border-[#CFE7E0] hover:border-[#7CC4B4]"
                      }`}
                    >
                      {variant === v && <Check className="w-3.5 h-3.5" />}
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between">
              <p className="text-sm font-bold text-[#004743]">Jumlah</p>
              <div className="flex items-center gap-3 bg-[#F0F7F5] border border-[#CFE7E0] rounded-full px-2 py-1.5">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-[#004743] hover:bg-[#E3F1ED] transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-extrabold text-[#004743] w-6 text-center">
                  {qty}
                </span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="w-8 h-8 rounded-full bg-[#0B7C6E] shadow-sm flex items-center justify-center text-white hover:bg-[#096A5E] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-6 bg-[#F0F7F5] rounded-2xl p-4 flex items-center justify-between">
              <span className="text-sm font-semibold text-[#4A6B63]">
                Subtotal
              </span>
              <span className="text-xl font-extrabold text-[#0B7C6E]">
                {formatRupiah(product.price * qty)}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={handleAddToCart}
                className="border-2 border-[#004743] text-[#004743] font-bold py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-[#004743] hover:text-white transition-all duration-300"
              >
                <ShoppingCart className="w-5 h-5" />
                + Keranjang
              </button>
              <button
                onClick={handleBuyNow}
                className="bg-[#FD7A18] hover:bg-[#E56A0E] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 hover:-translate-y-0.5 transition-all duration-300"
              >
                Beli Sekarang
              </button>
            </div>
          </div>
        </div>

        {/* RELATED */}
        <div className="mt-16">
          <h2 className="text-2xl font-extrabold text-[#004743] mb-6">
            Produk Lainnya
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => (
              <Link
                to={`/produk/${p.id}`}
                key={p.id}
                className="group bg-white rounded-[2rem] p-3 shadow-lg shadow-green-900/5 hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
              >
                <div
                  className="rounded-[1.5rem] overflow-hidden aspect-square"
                  style={{ backgroundColor: p.color }}
                >
                  <img
                    src={p.images?.[0]}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#004743] text-sm line-clamp-1">
                    {p.name}
                  </h3>
                  <p className="text-base font-extrabold text-[#0B7C6E] mt-1">
                    {formatRupiah(p.price)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
